/**
 * OnboardingProjectStep Component
 * Second step: Create a project
 */

import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { ProjectForm } from '@/components/projects/ProjectForm'
import { type ProjectFormData } from '@/lib/validations/project'
import { projectService, userService } from '@/services'
import { useAuth } from '@/hooks/useAuth'
import { useOnboarding } from '@/contexts/OnboardingContext.context'
import { Status, Priority, Horizon } from '@/types/enums'
import type { User } from '@/types/nodes'

interface OnboardingProjectStepProps {
  onSkip: () => void
  skipLabel?: string
}

export function OnboardingProjectStep({ onSkip }: OnboardingProjectStepProps) {
  const { user } = useAuth()
  const { workspaceId, setProjectId, setProjectName, nextStep } = useOnboarding()
  const [isLoading, setIsLoading] = useState(false)
  const [availableUsers, setAvailableUsers] = useState<User[]>([])

  // Fetch available users for the workspace
  useEffect(() => {
    const fetchAvailableUsers = async () => {
      if (workspaceId && user) {
        try {
          const users = await userService.getUsersByWorkspace(workspaceId)
          setAvailableUsers(users)
        } catch (error) {
          console.error('Failed to fetch available users:', error)
          // For onboarding, if we can't fetch users, use empty array
          setAvailableUsers([])
        }
      }
    }

    fetchAvailableUsers()
  }, [workspaceId, user])

  const onSubmit = async (data: ProjectFormData) => {
    if (!user) {
      toast.error('You must be logged in to create a project')
      return
    }

    if (!workspaceId) {
      toast.error('Workspace is required')
      return
    }

    setIsLoading(true)
    try {
      const createData = {
        name: data.name,
        description: data.description,
        workspaceId,
        status: Status.ToDo,
        ...(data.priority && { priority: data.priority }),
        ...(data.horizon && { horizon: data.horizon }),
      }

      const project = await projectService.createProject(createData, user.id)

      setProjectId(project.id)
      setProjectName(data.name)
      toast.success('Project created successfully!')
      nextStep()
    } catch (error) {
      console.error('Failed to create project:', error)
      toast.error('Failed to create project')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <ProjectForm
      defaultValues={{
        name: '',
        description: '',
        workspaceId: workspaceId || '',
        status: Status.ToDo,
        priority: Priority.None,
        horizon: Horizon.None,
      }}
      workspaces={[]}
      availableUsers={availableUsers}
      onSubmit={onSubmit}
      onCancel={onSkip}
      submitText={isLoading ? 'Creating Project...' : 'Create Project & Continue'}
      cancelText="Skip Project Creation"
      loading={isLoading}
      title="Create Your First Project"
      description="Projects help you organize related tasks and track progress on specific initiatives"
      simplified={true}
    />
  )
}
